import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { logService } from "../utils/logService";
import { useAuth } from "../hooks/useAuth";
import { toast } from "react-toastify";

const AddUser = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "User",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Display name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    // Store admin credentials before creating new user
    const adminEmail = currentUser?.email;
    const adminPassword = prompt(
      "Please enter your admin password to confirm user creation:"
    );

    if (!adminPassword) {
      setLoading(false);
      toast.error("Admin password required to create user.");
      return;
    }

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      await updateProfile(user, { displayName: formData.displayName });

      // Create user document in Firestore
      const userDoc = {
        email: formData.email,
        displayName: formData.displayName,
        role: formData.role,
        status: "Active",
        verificationLevel: "Verified",
        createdAt: new Date().toISOString().split("T")[0],
        created_by_name:
          currentUser?.displayName || currentUser?.email || "Admin",
        lastLogin: "Never",
        agentCount: 0,
      };

      await addDoc(collection(db, "users"), userDoc);

      // Sign out the newly created user
      await signOut(auth);

      // Sign back in as the admin
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

      // Log the user creation
      await logService.createLog(
        logService.actions.USER_ADDED_BY_ADMIN,
        {
          newUserId: userCredential.user.uid,
          newUserEmail: formData.email,
          newUserDisplayName: formData.displayName,
          newUserRole: formData.role,
        },
        {
          uid: currentUser?.uid,
          email: currentUser?.email,
          displayName: currentUser?.displayName,
        }
      );

      console.log("User created successfully:", userCredential.user.uid);

      toast.success(`User ${formData.displayName} created successfully!`);

      // Navigate back to users list
      navigate("/users");
    } catch (error) {
      console.error("Error creating user:", error);
      setErrors({ submit: error.message });
      toast.error("Failed to create user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-valia-primary mb-2">
              Add New User
            </h1>
            <p className="text-valia-gray-600 text-sm sm:text-base">
              Create a new user account
            </p>
          </div>
          <button
            onClick={() => navigate("/users")}
            className="valia-btn-secondary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Users
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-valia-background-secondary rounded-valia-lg">
        <form onSubmit={handleSubmit} className="mx-auto space-y-6">
          {/* Display Name */}
          <div>
            <label className="valia-label-required">Name</label>
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className={`valia-input ${
                errors.displayName ? "valia-input-error" : ""
              }`}
              placeholder="Enter display name"
            />
            {errors.displayName && (
              <p className="text-valia-error text-sm mt-1">
                {errors.displayName}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="valia-label-required">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`valia-input ${
                errors.email ? "valia-input-error" : ""
              }`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="text-valia-error text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="valia-label-required">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`valia-input ${
                errors.password ? "valia-input-error" : ""
              }`}
              placeholder="Enter password (min 6 characters)"
            />
            {errors.password && (
              <p className="text-valia-error text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="valia-label-required">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`valia-input ${
                errors.confirmPassword ? "valia-input-error" : ""
              }`}
              placeholder="Confirm password"
            />
            {errors.confirmPassword && (
              <p className="text-valia-error text-sm mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="valia-label-required">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="valia-input"
            >
              <option value="User">User</option>
              <option value="Moderator">Moderator</option>
              <option value="Admin">Admin</option>
            </select>
            <p className="text-valia-gray-500 text-sm mt-1">
              Select the appropriate role for the new user
            </p>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-valia-error/10 border border-valia-error rounded-valia p-3">
              <p className="text-valia-error text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="valia-btn-secondary valia-btn-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`valia-btn-primary valia-btn-sm ${
                loading ? "valia-btn-loading" : ""
              }`}
            >
              {loading ? (
                <>
                  <div className="valia-spinner w-4 h-4 mr-2"></div>
                  Creating User...
                </>
              ) : (
                "Create User"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUser;
